import type { DisplayZone } from "@/shared/datetime";

import { DAY, HOUR, MINUTE, SECOND, type TimeWindow } from "./window";

export interface AxisTick {
  /** Milliseconds since the epoch. */
  time: number;
  label: string;
  /** A day, month or year boundary — drawn with a full-height gridline. */
  major: boolean;
}

/**
 * How the axis turns an instant into text. Injected so `computeTicks` stays a
 * calculation with no hidden input from the user's datetime preference
 * registry, and so tests pass a fixed formatter.
 */
export interface TickFormatter {
  /**
   * The zone every label is read in (`undefined`: the viewer's local zone).
   * Ticks align to the same clock, so a label never lands between hours.
   */
  zone: DisplayZone | undefined;
  /** Clock label for a sub-day tick, e.g. "14:30" or "14:30:05". */
  clock: (date: Date, precision: "minutes" | "seconds") => string;
  /** Short date label, e.g. "Oct 4". */
  day: (date: Date) => string;
  /** Month label, e.g. "Oct". */
  month: (date: Date) => string;
  /** Year label, e.g. "2026". */
  year: (date: Date) => string;
}

const SUB_DAY_STEPS = [
  ...[1, 2, 5, 10, 15, 30].map((s) => s * SECOND),
  ...[1, 2, 5, 10, 15, 30].map((m) => m * MINUTE),
  ...[1, 2, 3, 6, 12].map((h) => h * HOUR),
];
const DAY_STEPS = [1, 2, 7];
const MONTH_STEPS = [1, 3, 6, 12, 24, 60, 120];
const MAX_TICKS = 400;
/** Labels are placed no closer than this many pixels apart. */
const MIN_LABEL_PX = 96;

interface CalendarFields {
  year: number;
  /** 0–11. */
  month: number;
  /** Day of the month, 1–31. */
  day: number;
  /** 0 = Sunday. */
  weekday: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/** The calendar and clock the ticks are aligned to: the viewer's local zone, or UTC. */
interface Calendar {
  /** How far this zone's clock is ahead of UTC at `ms`, in milliseconds. */
  offset: (ms: number) => number;
  read: (ms: number) => CalendarFields;
  /** Midnight starting the given day; a day or month out of range rolls over, as `Date` does. */
  midnight: (year: number, month: number, day: number) => number;
}

const LOCAL_CALENDAR: Calendar = {
  offset: (ms) => -new Date(ms).getTimezoneOffset() * MINUTE,
  read: (ms) => {
    const date = new Date(ms);
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
      weekday: date.getDay(),
      hours: date.getHours(),
      minutes: date.getMinutes(),
      seconds: date.getSeconds(),
    };
  },
  midnight: (year, month, day) => new Date(year, month, day).getTime(),
};

const UTC_CALENDAR: Calendar = {
  offset: () => 0,
  read: (ms) => {
    const date = new Date(ms);
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth(),
      day: date.getUTCDate(),
      weekday: date.getUTCDay(),
      hours: date.getUTCHours(),
      minutes: date.getUTCMinutes(),
      seconds: date.getUTCSeconds(),
    };
  },
  midnight: (year, month, day) => Date.UTC(year, month, day),
};

/**
 * Evenly spaced, round-number axis ticks for `window` on a panel `widthPx`
 * wide: seconds, minutes or hours within a day; whole days; or months and
 * years. Ticks follow the clock of `formatter.zone`: sub-day ticks land on
 * multiples of that clock (so "15:00" rather than "14:37"), day and month
 * ticks on its midnights.
 */
export function computeTicks(
  window: TimeWindow,
  widthPx: number,
  formatter: TickFormatter,
): AxisTick[] {
  const { start, end } = window;
  if (widthPx <= 0 || end <= start) {
    return [];
  }
  const calendar = formatter.zone === "UTC" ? UTC_CALENDAR : LOCAL_CALENDAR;
  const target = ((end - start) / widthPx) * MIN_LABEL_PX;
  const ticks: AxisTick[] = [];

  const subDay = SUB_DAY_STEPS.find((step) => step >= target);
  if (subDay !== undefined) {
    // Offset by the zone so multiples of the step fall on that clock's
    // hour/minute boundaries, not UTC's.
    const offset = calendar.offset(start);
    let t = Math.ceil((start + offset) / subDay) * subDay - offset;
    for (let guard = 0; t <= end && guard < MAX_TICKS; t += subDay, guard++) {
      const fields = calendar.read(t);
      const midnight =
        fields.hours === 0 && fields.minutes === 0 && fields.seconds === 0;
      const date = new Date(t);
      ticks.push({
        time: t,
        major: midnight,
        label: midnight
          ? formatter.day(date)
          : formatter.clock(date, subDay < MINUTE ? "seconds" : "minutes"),
      });
    }
    return ticks;
  }

  const dayStep = DAY_STEPS.find((days) => days * DAY >= target);
  if (dayStep !== undefined) {
    const first = calendar.read(start);
    let t = calendar.midnight(first.year, first.month, first.day);
    for (let guard = 0; t <= end && guard < MAX_TICKS; guard++) {
      const fields = calendar.read(t);
      const include =
        dayStep === 1 ||
        (dayStep === 2 ? fields.day % 2 === 1 : fields.weekday === 1);
      if (t >= start && include) {
        const newYear = fields.month === 0 && fields.day === 1;
        const date = new Date(t);
        ticks.push({
          time: t,
          major: fields.day === 1,
          label: newYear ? formatter.year(date) : formatter.day(date),
        });
      }
      t = calendar.midnight(fields.year, fields.month, fields.day + 1);
    }
    return ticks;
  }

  const monthStep =
    MONTH_STEPS.find((months) => months * 30.44 * DAY >= target) ?? 240;
  const first = calendar.read(start);
  let year = first.year;
  let month = first.month;
  for (let guard = 0; guard < MAX_TICKS; guard++) {
    const t = calendar.midnight(year, month, 1);
    if (t > end) {
      break;
    }
    const include =
      monthStep < 12
        ? (year * 12 + month) % monthStep === 0
        : month === 0 && year % (monthStep / 12) === 0;
    if (t >= start && include) {
      const date = new Date(t);
      ticks.push({
        time: t,
        major: month === 0,
        label: month === 0 ? formatter.year(date) : formatter.month(date),
      });
    }
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return ticks;
}
